import { useWeb3React } from '@web3-react/core';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useActiveWeb3React } from '../../../../hooks';
import { useGetYetiswapMarketCommissionRateNonYTS, useGetYetiswapMarketCommissionRateYTS } from '../../../../hooks/useNFTMarketPlace';
import { getTokenPrice } from '../../../../services/NFTMarketPlace/ApiCall';
import { getAccountCollectionNFTItems, getAccountHistory, getAccountOnSaleNFTItems } from '../../../../services/NFTMarketPlace/Graphquery';
import NFTComboBox from '../../components/NFTCombobox';
import NFTWalletCard from '../../components/NFTWalletCard';
import Scrollbar from '../../components/Scrollbar';
import useNFTCollectionList from '../../data/useNFTCollectionList';
import { loadNFTLikeCounts } from '../../data/useNFTLikeCount';
import { INFTCollectionItems } from '../../models/INFTCollectionItems';
import { INFTHistory } from '../../models/INFTHistory';
import INFTSalesList from '../../models/INFTSalesList';
import IPriceInfo from '../../models/IPriceInfo';
import Empty from '../../newComponents/Empty';
import NFTList from '../../newComponents/NFTList';
import NFTTabs from '../../newComponents/NFTTabs';
import NFTActivityFormater from '../../nftComponents/Activities';
import { defaultCollection } from '../../nftComponents/Details';
import NFTMarketItem from '../Market/components/NFTMarketItem';
import ProfileHeader, { profileTabItems } from './ProfileHeader';
import { ProfilePageContent, ProfilePageItemsWrapper, ProfilePageWrapper } from './styled';


const ProfilePage = (props: any) => {
    const { chainId } = useActiveWeb3React()
    const { account } = useWeb3React()
    const { accountParam } = useParams<{ accountParam: string }>()
    const userAccount = accountParam || account || ''

    const collections = useNFTCollectionList();
    const location = useLocation();
    const history = useHistory();
    const searchParam = new URLSearchParams(location.search).get('tab');
    const [activeTab, setActiveTab] = React.useState(searchParam && profileTabItems.findIndex(i => i.key == searchParam) > -1 ? searchParam : profileTabItems[0].key);

    const [saleList, setOnSaleList] = React.useState<INFTSalesList[]>([])
    const [collectionList, setCollectionList] = React.useState<INFTCollectionItems[]>([])
    const [historyList, setHistoryList] = React.useState<INFTHistory[]>([])
    const [priceInfo, setPriceInfo] = React.useState<IPriceInfo | null>(null)

    const yetiswapMarketCommissionRateYTS = useGetYetiswapMarketCommissionRateYTS()
    const yetiswapMarketCommissionRateNonYTS = useGetYetiswapMarketCommissionRateNonYTS()

    const [filteredCollectionKey, setFilteredCollectionKey] = React.useState<string>("ALL");

    React.useEffect(() => {
        getTokenPrice().then((res: any) => setPriceInfo(res))
    }, []);

    const getSalesNFTItems = React.useCallback(() => {
        getAccountOnSaleNFTItems(userAccount).then((response: INFTSalesList[] | null) => {
            if (response) {
                loadNFTLikeCounts(response.map((r: INFTSalesList) => ({ collectionAddress: r.tokenContract, tokenID: r.tokenId })));
                setOnSaleList(response)
            }
        })
    }, [userAccount])

    const getCollectionNFTItems = React.useCallback(() => {
        getAccountCollectionNFTItems(
            userAccount,
            collections.map((item) => `"${item.tokenAddress.toLowerCase()}"`)
        ).then((response: INFTCollectionItems[] | null) => {
            if (response) {
                loadNFTLikeCounts(response.map((r: INFTCollectionItems) => ({ collectionAddress: r.contract.id, tokenID: +r.tokenID })));
                setCollectionList(response)
            }
        })
    }, [userAccount, collections])

    const getUserAccountHistory = React.useCallback(
        (type: string, skip: number, historyData: INFTHistory[], sortItems?: string, currency?: string, nftCollection?: string) => {
            getAccountHistory(userAccount, type, skip, sortItems, currency, nftCollection).then(
                (response: INFTHistory[] | null) => {
                    if (response) {
                        if (skip === 0) {
                            setHistoryList(response)
                        } else {
                            setHistoryList([...historyData, ...response])
                        }
                    }
                }
            )
        },
        [userAccount]
    )

    const getNFTList = React.useCallback(() => {
        if (userAccount !== '') {
            getSalesNFTItems()
            getCollectionNFTItems()
            getUserAccountHistory('All', 0, historyList)
        }
    }, [getSalesNFTItems, getCollectionNFTItems, getUserAccountHistory, userAccount])

    React.useEffect(() => {
        if (userAccount !== '') {
            getNFTList()
        }
    }, [getNFTList, userAccount])

    const collectionFilterItems = React.useMemo(() => {
        let items = collections.map((item: any) => {
            return {
                key: item.tokenAddress.toString(),
                text: item.name,
            }
        });
        items.unshift({ key: 'ALL', text: 'All' });
        return items;
    }, [collections]);

    const onCollectionFilterChange = React.useCallback((item: any) => {
        setFilteredCollectionKey(item.key);
    }, []);

    const renderList = React.useMemo(() => {
        return (id: string, children: any, length: number) => {
            if (length == 0) return <Empty size='large' />
            return (
                <>
                    {id == "profileCollectionList" &&
                        <NFTComboBox
                            items={collectionFilterItems}
                            defaultKey={filteredCollectionKey}
                            onSelectedChange={onCollectionFilterChange}
                            textPrefix='Collection: '
                            allowSearch
                        />
                    }
                    <ProfilePageItemsWrapper>
                        {children}
                    </ProfilePageItemsWrapper>
                </>
            )
        }
    }, [collectionFilterItems]);


    const renderTabContent = (key: string) => {
        switch (key) {
            case "collection":
                let items = collectionList.filter(item => filteredCollectionKey !== "ALL" ? item.contract.id.toString() == filteredCollectionKey : true)
                return renderList("profileCollectionList", items.map((item, index) => (
                    <NFTWalletCard
                        key={`${index}_WALLET_CARD_${item.tokenID}`}
                        contractAddress={item.contract.id}
                        getSalesNFTItems={getNFTList}
                        tokenId={+item.tokenID}
                        owner={item.owner.id}
                        yetiswapMarketCommissionRateYTS={yetiswapMarketCommissionRateYTS}
                        yetiswapMarketCommissionRateNonYTS={yetiswapMarketCommissionRateNonYTS}
                        priceInfo={priceInfo} />
                )), items.length);
            case "activity":
                return (
                    <div style={{ display: "flex", flexDirection: 'column', width: '100%', paddingTop: '2rem' }}>
                        {historyList.map((item) => (
                            <NFTActivityFormater key={`${item.id}_HISTORY_ITEM`} historyItem={item} chainId={chainId} />
                        ))}
                    </div>
                )
            default:
                return renderList("profileOnSale", saleList.map((item: INFTSalesList) => (
                    <NFTMarketItem
                        key={`${item.id}_${item.tokenContract}_WALLET`}
                        item={item}
                        getSalesNFTItems={getSalesNFTItems}
                        account={account || ""}
                        priceInfo={priceInfo}
                    />
                )), saleList.length);
        }
    }

    const onTabChange = (key: string) => {
        setActiveTab(key);
        history.push(`/profile/${accountParam ?? "wallet"}?tab=${key}`);
    }

    return (
        <Scrollbar>
            <ProfilePageWrapper>
                <Helmet>
                    <title>{"Profile - YetiSwap NFT Marketplace"}</title>
                </Helmet>
                {userAccount ? (
                    <>
                        <ProfileHeader activeTab={activeTab} onTabChange={onTabChange} />
                        <ProfilePageContent id={"profilePageContent"}>
                            <NFTTabs tabs={profileTabItems} renderTabContent={renderTabContent} noHeader activeTab={activeTab} noScrollbar />
                        </ProfilePageContent>
                    </>
                ) : (<h1>Connect your wallet to see this page</h1>)
                }
            </ProfilePageWrapper>
        </Scrollbar>
    )
}
export default ProfilePage; 